

import { FC, useEffect, useRef, useState } from "react";
import { CellWrapper, useCellContext, useDoubleTouch, isValidKey } from '@silevis/reactgrid'
import { shortcutType } from "./reactGridHelper";
import Select, { components } from "react-select";
import styles from './CustomSelectCell.module.scss'
import zIndex from "@mui/material/styles/zIndex";
import { position } from "html2canvas/dist/types/css/property-descriptors/position";

interface TextCellProps {
    value: any;
    onValueChanged: (newText: string) => void;
    style?: React.CSSProperties;
    onAddRow?: (rowIndex: number) => void;
    onDeleteRow?: (rowIndex: number) => void;
    allowShortcuts?: shortcutType[];
    options: any[];
    optionLabelKey: string;
}

const fontStyles = {
    'sm': {
        fontFamily: "Work Sans",
        fontSize: "0.6875rem",
        fontStyle: "normal",
        fontWeight: "500",
        lineHeight: "0.875rem", /* 127.273% */
        letterSpacing: "0.00688rem",
    },
    'md': {
        fontFamily: "Work Sans",
        fontSize: "0.8125rem",
        fontStyle: "normal",
        fontWeight: "500",
        lineHeight: "1.125rem", /* 128.571% */
        letterSpacing: "0.00875rem",
    }
}
const heightStyles = {
    'sm': {
        height: "20px",
    },
    'md': {
        height: "42px",
    }
}


const customStyles = (error, touched, size) => ({
    control: (provided, state) => ({
        ...provided,
        borderRadius: "0.5rem",
        border: 'none',
        // margin: 8,
        // border: error && touched ? "1px solid #E94F4F" : "1px solid #d4d4d4",
        outline: 0,
        // padding: "0.15rem 0.3rem",
        // height: "40px",
        ...heightStyles[size],
        boxShadow: "none",
        width: '100%',
        // fontFamily: "Work Sans",
        // fontSize: "0.875rem",
        // fontStyle: "normal",
        // fontWeight: "500",
        // lineHeight: "1.125rem", /* 128.571% */
        // letterSpacing: "0.00875rem",
        backgroundColor: "transparent",
        ...fontStyles[size],

        ":hover": {
            cursor: 'pointer',
            backgroundColor: 'transparent',
            border: 'none'
        }
    }),
    menu: (provided) => ({
        ...provided,
        backgroundColor: "#fff",
        zIndex: 99999,
        // positon: 'relative',
        borderRadius: "0.5rem",
    }),
    menuPortal: (base) => ({
        ...base,
        // zIndex: 9999,
        zIndex: 99999,
        position: 'fixed'
    }),
    menuList: (base) => ({
        ...base,
        "::-webkit-scrollbar": {
            width: "5px",
            height: "0px",
        },
    }),
    option: (styles, { data, isDisabled, isFocused, isSelected }) => {
        return {
            ...styles,
            color: "#212121",
            backgroundColor: isFocused ? "#F0EFF4" : "transparent",
            // "fontFamily": "Work Sans",
            // "fontSize": "0.875rem",
            // "fontStyle": "normal",
            // "fontWeight": "500",
            // "lineHeight": "1.125rem", /* 128.571% */
            // "letterSpacing": "0.00875rem",
            ...fontStyles[size],

            '&:hover': {
                cursor: "pointer"
            },

            ':active': {
                backgroundColor: "#F0EFF4",
            }
        }
    },
    noOptionsMessage: (styles) => {
        return {
            ...styles,
            color: "#56595C",
            // "fontFamily": "Work Sans",
            // "fontSize": "0.875rem",
            // "fontStyle": "normal",
            // "fontWeight": "500",
            // "lineHeight": "1.125rem", /* 128.571% */
            // "letterSpacing": "0.00875rem",
            ...fontStyles[size],
        }
    },
    placeholder: (styles) => ({
        ...styles,
        color: "#aaaaaa",
        // "fontFamily": "Work Sans",
        // "fontSize": "0.875rem",
        // "fontStyle": "normal",
        // "fontWeight": "400",
        // "lineHeight": "1.125rem", /* 128.571% */
        // "letterSpacing": "0.00875rem",
        ...fontStyles[size],
    }),
    indicatorSeparator: () => ({
        display: 'none',
    }),
})

export const CustomSelectCell: FC<TextCellProps> = ({ value, onValueChanged, style, onAddRow, onDeleteRow, allowShortcuts, options, optionLabelKey }) => {
    const ctx = useCellContext();
    const targetSelectRef = useRef(null);
    const [isEditMode, setEditMode] = useState(false);
    const [currentValue, setCurrentValue] = useState(value);
    const escapePressedRef = useRef(false);
    const { handleDoubleTouch } = useDoubleTouch(ctx, setEditMode);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const selectContainerRef = useRef<HTMLDivElement>(null);

    // console.log('select value', value);

    useEffect(() => {
        setCurrentValue(value);
    }, [value]);

    useEffect(() => {
        if (isEditMode) {
            targetSelectRef.current.focus();
        } else {
            targetSelectRef.current.blur();
        }
    }, [isEditMode])


    useEffect(() => {
        if (!ctx.isFocused && isMenuOpen) {
            // targetSelectRef.current.blur();
            setIsMenuOpen(false);
            setEditMode(false);
        }
    }, [ctx.isFocused])



    // outside-click detector
    useEffect(() => {
        if (!isMenuOpen) return;

        const onDocMouseDown = (e: MouseEvent) => {
            const target = e.target as HTMLElement;

            // const selectContains = selectContainerRef.current?.contains(target);
            // console.log('selectContains', selectContains);
            // 1) click inside our container? ignore
            if (selectContainerRef.current?.contains(target)) return;

            // const targetContains = target.closest(".my-sel__menu");
            // console.log('targetContains', targetContains);
            // 2) click inside the RS menu? (use the same prefix you pass below)
            if (target.closest(".my-sel__menu")) return;

            // otherwise, it’s “outside”
            setIsMenuOpen(false);
            setEditMode(false);
        };

        document.addEventListener("mousedown", onDocMouseDown);
        return () => document.removeEventListener("mousedown", onDocMouseDown);
    }, [isMenuOpen]);

    useEffect(() => {
        if (!ctx.isFocused) return; // Ensure shortcut only works when cell is focused

        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === "+" || e.key === "=")) {
                e.preventDefault();
                if (allowShortcuts.includes(shortcutType.addRow)) {
                    onAddRow?.(ctx.realRowIndex - 1);
                }
            } else if ((e.ctrlKey || e.metaKey) && e.key === "-") {
                e.preventDefault();
                if (allowShortcuts.includes(shortcutType.deleteRow)) {
                    onDeleteRow?.(ctx.realRowIndex - 1);
                }
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [ctx.isFocused, onAddRow, onDeleteRow, allowShortcuts]);

    const arrowKeys = new Set(["ArrowUp", "ArrowDown"]);

    return (
        <CellWrapper
            // onStringValueRequested={() => value}
            onStringValueRequested={() => {
                if (!currentValue) return "";
                return typeof currentValue === "object" ? currentValue[optionLabelKey] || JSON.stringify(currentValue) : currentValue;
            }}
            onStringValueReceived={(v) => {
                const matchedOption = options.find((option) => option[optionLabelKey] === v);
                onValueChanged?.(matchedOption || v);
            }}
            onTouchEnd={handleDoubleTouch}
            style={style}
            onDoubleClick={(e) => {
                e.stopPropagation();
                if (ctx.isFocused) {
                    setCurrentValue(value);
                    setIsMenuOpen(true);  // Ensure menu opens
                    setEditMode(true);
                }
            }}
            // onMouseDown={e => {
            //     // 1) prevent React-Grid from stealing focus
            //     e.stopPropagation();
            //     // 2) flip into edit-mode before the native focus
            //     setEditMode(true);
            // }}
            onClick={(e) => {
                e.stopPropagation();
                console.log('on click cell', e);
                if (ctx.isFocused && !isMenuOpen) {
                    setCurrentValue(value);
                    setIsMenuOpen(true);  // Ensure menu opens
                    setEditMode(true);
                }
            }}
            onKeyDown={(e) => {
                console.log('on key down cell', e.key, 'edit mode', isEditMode);
                // console.log('key down', e.key);
                // console.log('ctx', ctx);
                // console.log('isEditMode', isEditMode);
                if (!isEditMode && isValidKey(e, [])) {
                    e.stopPropagation();
                    setCurrentValue("");
                    setEditMode(true);
                } else if (!isEditMode && !ctx.isSelected && (e.key === "Enter" || e.key === "F2")) {
                    e.stopPropagation();
                    setCurrentValue(value);
                    setEditMode(true);
                    setIsMenuOpen(true);  // Ensure menu opens
                } else if (isEditMode && isMenuOpen && arrowKeys.has(e.key)) {
                    e.stopPropagation();
                }
            }}
        >
            <div
                ref={selectContainerRef}
                className={styles.selectFieldContainer}>
                <Select
                    options={options}
                    classNamePrefix="my-sel"
                    styles={customStyles('', false, 'md')}
                    isClearable={false}
                    isSearchable={false}
                    placeholder={''}
                    value={currentValue}
                    ref={targetSelectRef}
                    onFocus={(e) => {
                        console.log('select on focus');
                        setIsMenuOpen(true);
                    }}
                    menuIsOpen={isMenuOpen}
                    onKeyDown={(e) => {
                        console.log('on key down select cell', e.key);
                        if (e.key === "Enter") {
                            e.stopPropagation();
                            setIsMenuOpen(true); // ✅ Reopen the menu when Enter is pressed after selection
                        }
                    }}
                    // onBlur={(e) => {
                    //     e.stopPropagation();
                    //     console.log('event on blur select cell', e);
                    //     setIsMenuOpen(false);
                    //     setEditMode(false);
                    // }}
                    // onFocus={() => setIsMenuOpen(true)} // Open menu on focus
                    // onBlur={(e) => {
                    //     console.log('triggering on blur select cell')
                    //     setIsMenuOpen(false);
                    //     setEditMode(false);
                    // }} // Close menu when clicking outside
                    onChange={(newValue, actionMeta) => {
                        console.log('newValue', newValue);
                        console.log('actionMeta', actionMeta);
                        onValueChanged?.(newValue);
                        setIsMenuOpen(false);
                        setEditMode(false);
                    }}
                    getOptionLabel={(option) => option[optionLabelKey]}
                    // menuPortalTarget={document.body}
                    blurInputOnSelect={false}
                    menuPlacement='auto'
                    menuPosition="fixed"
                // menuShouldBlockScroll={true}
                />
            </div>

            {/* {isEditMode ? (
                <input
                    className="rg-input"
                    value={currentValue}
                    onChange={(e) => setCurrentValue(e.currentTarget.value)}
                    onBlur={(e) => {
                        console.log('triggering on blur text cell')
                        if (!escapePressedRef.current) {
                            onTextChanged?.(e.currentTarget.value);
                        }
                        setEditMode(false);
                        if (escapePressedRef.current) {
                            escapePressedRef.current = false;
                        }
                    }}
                    onCut={(e) => e.stopPropagation()}
                    onCopy={(e) => e.stopPropagation()}
                    onPaste={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                        const controlKeys = ["Escape", "Enter", "Tab"];
                        if (!controlKeys.includes(e.key)) {
                            e.stopPropagation();
                        }
                        if (e.key === "Escape") {
                            escapePressedRef.current = true;
                            setEditMode(false);
                        } else if (e.key === "Enter") {
                            // setCurrentValue(e.currentTarget.value)
                            onTextChanged?.(e.currentTarget.value);
                            setEditMode(false);
                        } else if (e.key === "Tab") {
                            // setCurrentValue(e.currentTarget.value)
                            onTextChanged?.(e.currentTarget.value);
                            setEditMode(false);
                        }
                    }}
                    autoFocus
                    ref={targetInputRef}
                />
            ) : (
                initialText
            )} */}
        </CellWrapper>
    );
};